// src/pages/MisCertificados.js
//
// Página del estudiante autenticado para ver sus certificados emitidos.
// URL: /mis-certificados
//
// Muestra una tarjeta compacta por cada certificado con:
//   - Curso, institución, calificación, fecha, estado
//   - Botón "Ver y descargar" que abre un Dialog con la tarjeta
//     grande + QR + botones de imprimir / descargar PNG
//   - Botón "Verificar" que abre la ruta pública /cert/{codigo} en
//     nueva pestaña (útil para que el estudiante compruebe que su QR
//     funciona antes de compartir el certificado con un tercero).

import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Container, Paper, Typography, Grid, Button, Chip,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Stack, Divider,
} from '@mui/material';
import {
  Verified, HourglassEmpty, Block, Visibility, Download,
  Print, OpenInNew, Close, School,
} from '@mui/icons-material';
import { certificadoService } from '../services/api';
import CertificadoCard, {
  descargarCertificadoPng, imprimirCertificado,
} from '../components/certificado/CertificadoCard';

const ESTADO_META = {
  VALIDO:   { label: 'Válido',    color: 'success', icon: <Verified fontSize="small" /> },
  RETENIDO: { label: 'En revisión', color: 'warning', icon: <HourglassEmpty fontSize="small" /> },
  REVOCADO: { label: 'Revocado',  color: 'error',   icon: <Block fontSize="small" /> },
};

export default function MisCertificados() {
  const [state, setState] = useState({ loading: true, items: [], error: '' });
  const [verDetalle, setVerDetalle] = useState(null);   // { cert, plantilla, urlValidacion }
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const items = await certificadoService.getMios();
        setState({ loading: false, items: items || [], error: '' });
      } catch (err) {
        setState({ loading: false, items: [], error: err.message || 'Error cargando certificados.' });
      }
    })();
  }, []);

  const abrirDetalle = async (item) => {
    setCargandoDetalle(true);
    try {
      // Para tener los datos completos de la plantilla (color, firma, logo)
      // reusamos el endpoint público de validación. Lo que ve el estudiante
      // es la misma data que vería cualquiera que escanee el QR.
      const data = await certificadoService.validar(item.codigo);
      const urlValidacion = `${window.location.origin}/cert/${item.codigo}`;
      setVerDetalle({ cert: data.certificado, plantilla: data.plantilla, urlValidacion });
    } catch (err) {
      alert('No se pudo cargar el detalle: ' + (err.message || 'error'));
    } finally {
      setCargandoDetalle(false);
    }
  };

  const cerrarDetalle = () => setVerDetalle(null);

  // ────────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <School sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Mis certificados</Typography>
      </Box>

      {state.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : state.error ? (
        <Alert severity="error">{state.error}</Alert>
      ) : state.items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <School sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Aún no tienes certificados</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Completa un curso al 100% para recibir tu certificado académico automáticamente.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {state.items.map((item) => {
            const meta = ESTADO_META[item.estado] || ESTADO_META.VALIDO;
            return (
              <Grid item xs={12} md={6} key={item.id}>
                <Paper sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {item.cursoTitulo}
                      </Typography>
                      {item.institucionNombre && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {item.institucionNombre}
                        </Typography>
                      )}
                    </Box>
                    <Chip icon={meta.icon} label={meta.label} color={meta.color} size="small" />
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack spacing={0.75} sx={{ mb: 2, flexGrow: 1 }}>
                    <Dato label="Código"
                          value={item.codigo}
                          mono />
                    <Dato label="Emitido"
                          value={new Date(item.fechaEmision).toLocaleDateString('es-CO', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          })} />
                    {item.cursoDuracion && <Dato label="Duración" value={item.cursoDuracion} />}
                    {item.calificacion != null && (
                      <Dato label="Calificación" value={`${Number(item.calificacion).toFixed(1)}`} />
                    )}
                  </Stack>

                  {item.estado === 'RETENIDO' && (
                    <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
                      En revisión. Un administrador debe aprobarlo antes de que esté disponible públicamente.
                    </Alert>
                  )}

                  {item.estado === 'REVOCADO' && (
                    <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
                      Este certificado fue revocado.
                    </Alert>
                  )}

                  <Stack direction="row" spacing={1}>
                    <Button fullWidth variant="contained" startIcon={<Visibility />}
                            disabled={cargandoDetalle || item.estado === 'REVOCADO'}
                            onClick={() => abrirDetalle(item)}>
                      Ver y descargar
                    </Button>
                    <IconButton title="Verificar públicamente"
                                onClick={() => window.open(`/cert/${item.codigo}`, '_blank')}>
                      <OpenInNew />
                    </IconButton>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ────────── Dialog de vista completa ────────── */}
      <Dialog open={!!verDetalle} onClose={cerrarDetalle} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Vista del certificado
          <IconButton onClick={cerrarDetalle}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {verDetalle && (
            <Box sx={{ overflow: 'auto', display: 'flex', justifyContent: 'center', py: 2 }}>
              <Box sx={{
                transform: { xs: 'scale(0.35)', sm: 'scale(0.5)', md: 'scale(0.65)', lg: 'scale(0.8)' },
                transformOrigin: 'top center',
                mb: { xs: '-460px', sm: '-350px', md: '-250px', lg: '-140px' },
              }}>
                <CertificadoCard
                  ref={cardRef}
                  cert={verDetalle.cert}
                  plantilla={verDetalle.plantilla}
                  urlValidacion={verDetalle.urlValidacion}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDetalle}>Cerrar</Button>
          <Button startIcon={<Print />}
                  onClick={() => imprimirCertificado(cardRef.current, verDetalle?.cert?.codigo)}>
            Imprimir
          </Button>
          <Button variant="contained" startIcon={<Download />}
                  onClick={() => descargarCertificadoPng(cardRef.current, verDetalle?.cert?.codigo)}>
            Descargar PNG
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

function Dato({ label, value, mono }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{
        fontWeight: 500, textAlign: 'right',
        fontFamily: mono ? 'monospace' : undefined,
      }}>
        {value}
      </Typography>
    </Box>
  );
}
